import React, { useId, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { BookOpen, Briefcase, FileSpreadsheet, UserCircle } from 'lucide-react';
import servicesGuideScreen from '../../assets/Guide/ServicesGuide/image.png';
import servicesGuideStep1Modal from '../../assets/Guide/ServicesGuide/Step1.png';
import servicesGuideDestinoDomicilios from '../../assets/Guide/ServicesGuide/Step2DestinyDomicilios.png';
import servicesGuideDestinoRoute from '../../assets/Guide/ServicesGuide/StepDestinyRoute.png';
import servicesGuideOrigenDomicilios from '../../assets/Guide/ServicesGuide/StepOriginDomicilios.png';
import servicesGuideOrigenRoute from '../../assets/Guide/ServicesGuide/Step2OriginRoute.png';
import servicesGuideManual from '../../assets/Guide/ServicesGuide/Step2Manual.png';
import servicesGuideManualRoute from '../../assets/Guide/ServicesGuide/Step2ManualRoute.png';
import servicesGuideStep3Modal from '../../assets/Guide/ServicesGuide/Step3.png';
import passengerGuideList from '../../assets/Guide/PassengerGuide/Passenger.png';
import passengerGuideForm from '../../assets/Guide/PassengerGuide/PassengerFormNew.png';
import passengerGuideAddresses from '../../assets/Guide/PassengerGuide/AdressPassenger.png';
import './UsageGuidePage.css';
import ImageToExcelConverter from '../../components/Services/forms/ImageToExcelConverter';

type GuideTab = 'service' | 'passenger' | 'imageExcel' | 'update';

const UsageGuidePage: React.FC = () => {
  const baseId = useId();
  const location = useLocation();
  const initialTab = (location.state as { tab?: GuideTab } | null)?.tab;
  const [tab, setTab] = useState<GuideTab>(initialTab ?? 'service');

  const tabs: { id: GuideTab; label: string; icon: React.ReactNode }[] = [
    { id: 'service', label: 'Agregar un servicio', icon: <Briefcase size={18} aria-hidden /> },
    { id: 'passenger', label: 'Agregar un pasajero', icon: <UserCircle size={18} aria-hidden /> },
    { id: 'imageExcel', label: 'Imagen a Excel', icon: <FileSpreadsheet size={18} aria-hidden /> },
    // { id: 'update', label: 'Actualizar información', icon: <RefreshCw size={18} aria-hidden /> },
  ];

  return (
    <div className="usage-guide-page">
      <header className="users-header">
        <h1>Guía de uso</h1>
      </header>

      <p className="usage-guide-intro">
        Pasos alineados con las pantallas del sistema. Despliega cada paso para ver el detalle y usa
        los accesos directos para ir a la sección correspondiente.
      </p>

      <div
        className="usage-guide-tabs"
        role="tablist"
        aria-label="Temas de la guía"
      >
        {tabs.map((t) => {
          const selected = tab === t.id;
          const tabId = `${baseId}-tab-${t.id}`;
          const panelId = `${baseId}-panel-${t.id}`;
          return (
            <button
              key={t.id}
              type="button"
              role="tab"
              id={tabId}
              aria-selected={selected}
              aria-controls={panelId}
              tabIndex={selected ? 0 : -1}
              className="usage-guide-tab"
              onClick={() => setTab(t.id)}
            >
              {t.icon}
              {t.label}
            </button>
          );
        })}
      </div>

      {tab === 'service' && (
        <section
          id={`${baseId}-panel-service`}
          role="tabpanel"
          aria-labelledby={`${baseId}-tab-service`}
          className="usage-guide-panel"
        >
          <h2>Agregar un servicio</h2>
          <p className="usage-guide-panel-lead">
            Los servicios nuevos se crean desde la lista de Servicios con un asistente de tres pasos.
          </p>
          <ol className="usage-guide-steps">
            <li className="usage-guide-step">
              <details open>
                <summary>
                  <span className="usage-guide-step-num">1</span>
                  Abrir el formulario de creación
                </summary>
                <div className="usage-guide-step-body">
                  <p>
                    En el menú lateral entra a <strong>Servicios</strong> y pulsa el botón{' '}
                    <strong>+ Nuevo Servicio</strong>. Se abrirá el modal &quot;Crear Nuevo Servicio&quot;
                    con el indicador <strong>Paso 1 de 3</strong>.
                  </p>
                  <figure className="usage-guide-figure">
                    <img
                      src={servicesGuideScreen}
                      alt="Vista de la pantalla Servicios: menú lateral, filtros y botón Nuevo Servicio."
                      className="usage-guide-screenshot"
                      loading="lazy"
                      decoding="async"
                    />
                    <figcaption className="usage-guide-figure-caption">
                      Pantalla de Servicios: accede desde el menú y usa <strong>+ Nuevo Servicio</strong>.
                    </figcaption>
                  </figure>
                </div>
              </details>
            </li>
            <li className="usage-guide-step">
              <details>
                <summary>
                  <span className="usage-guide-step-num">2</span>
                  Paso 1 — Información básica
                </summary>
                <div className="usage-guide-step-body">
                  <p>
                    Completa <strong>Empresa</strong>, <strong>Fecha del servicio</strong> (solo hoy o
                    futuras) y <strong>Hora del servicio</strong>. La fecha y hora no pueden quedar en
                    el pasado respecto al momento actual.
                  </p>
                  <p>
                    Cuando los datos sean válidos, el botón <strong>Siguiente</strong> se habilitará.
                  </p>
                  <figure className="usage-guide-figure">
                    <img
                      src={servicesGuideStep1Modal}
                      alt="Modal Crear Nuevo Servicio: Paso 1 de 3, empresa, fecha y hora del servicio."
                      className="usage-guide-screenshot"
                      loading="lazy"
                      decoding="async"
                    />
                    <figcaption className="usage-guide-figure-caption">
                      Asistente en <strong>Paso 1 de 3</strong>: información básica (empresa, fecha y hora).
                    </figcaption>
                  </figure>
                </div>
              </details>
            </li>
            <li className="usage-guide-step">
              <details>
                <summary>
                  <span className="usage-guide-step-num">3</span>
                  Paso 2 — Ruta y pasajeros
                </summary>
                <div className="usage-guide-step-body">
                  <p>
                    En <strong>Paso 2 de 3</strong> del asistente elige el <strong>tipo de servicio</strong>.
                    El formulario cambia según el tipo: revisa la sección que corresponda a tu caso y
                    agrega al menos un pasajero según las reglas de ese tipo.
                  </p>

                  <div className="usage-guide-type-sections">
                    <section
                      className="usage-guide-type-section"
                      aria-labelledby={`${baseId}-type-destino`}
                    >
                      <h3 className="usage-guide-type-heading" id={`${baseId}-type-destino`}>
                        Destino domicilios
                      </h3>
                      <p>
                        El recorrido lleva a los pasajeros a un <strong>destino común</strong> (ruta o
                        punto del catálogo); cada pasajero define su <strong>origen</strong> en domicilio.
                        Completa la secuencia de pasajeros y sus direcciones de recogida según indique el
                        formulario.
                      </p>
                      <figure className="usage-guide-figure">
                        <img
                          src={servicesGuideDestinoDomicilios}
                          alt="Paso 2 del asistente: tipo destino en domicilios, ruta y tabla de pasajeros."
                          className="usage-guide-screenshot"
                          loading="lazy"
                          decoding="async"
                        />
                        <figcaption className="usage-guide-figure-caption">
                          Ejemplo de configuración con tipo <strong>destino en domicilios</strong>.
                        </figcaption>
                      </figure>
                      <h4 className="usage-guide-variant-heading">Misma variante por ruta</h4>
                      <figure className="usage-guide-figure">
                        <img
                          src={servicesGuideDestinoRoute}
                          alt="Paso 2: destino en domicilios cuando el destino común se elige como ruta."
                          className="usage-guide-screenshot"
                          loading="lazy"
                          decoding="async"
                        />
                        <figcaption className="usage-guide-figure-caption">
                          <strong>Destino en domicilios</strong> con destino común definido como{' '}
                          <strong>ruta</strong> en el catálogo.
                        </figcaption>
                      </figure>
                    </section>

                    <section
                      className="usage-guide-type-section"
                      aria-labelledby={`${baseId}-type-origen`}
                    >
                      <h3 className="usage-guide-type-heading" id={`${baseId}-type-origen`}>
                        Origen domicilios
                      </h3>
                      <p>
                        Los pasajeros comparten un <strong>origen común</strong> en el catálogo; cada uno
                        indica su <strong>destino</strong> en domicilio. Añade pasajeros y direcciones de
                        destino en el orden que requiera el servicio.
                      </p>
                      <figure className="usage-guide-figure">
                        <img
                          src={servicesGuideOrigenDomicilios}
                          alt="Paso 2 del asistente: tipo origen en domicilios y tabla de pasajeros."
                          className="usage-guide-screenshot"
                          loading="lazy"
                          decoding="async"
                        />
                        <figcaption className="usage-guide-figure-caption">
                          Ejemplo con tipo <strong>origen en domicilios</strong>.
                        </figcaption>
                      </figure>
                      <h4 className="usage-guide-variant-heading">Misma variante por ruta</h4>
                      <figure className="usage-guide-figure">
                        <img
                          src={servicesGuideOrigenRoute}
                          alt="Paso 2: origen en domicilios cuando el origen común se elige como ruta."
                          className="usage-guide-screenshot"
                          loading="lazy"
                          decoding="async"
                        />
                        <figcaption className="usage-guide-figure-caption">
                          <strong>Origen en domicilios</strong> con origen común definido como{' '}
                          <strong>ruta</strong> en el catálogo.
                        </figcaption>
                      </figure>
                    </section>

                    <section
                      className="usage-guide-type-section"
                      aria-labelledby={`${baseId}-type-manual`}
                    >
                      <h3 className="usage-guide-type-heading" id={`${baseId}-type-manual`}>
                        Manual
                      </h3>
                      <p>
                        Indicas <strong>origen</strong> y <strong>destino</strong> desde el catálogo (o la
                        variante que muestre el selector) y defines la tabla de pasajeros sin el esquema
                        domicilio común anterior. Revisa que origen, destino y pasajeros queden coherentes
                        antes de continuar.
                      </p>
                      <figure className="usage-guide-figure">
                        <img
                          src={servicesGuideManual}
                          alt="Paso 2 del asistente: tipo manual, origen, destino y pasajeros."
                          className="usage-guide-screenshot"
                          loading="lazy"
                          decoding="async"
                        />
                        <figcaption className="usage-guide-figure-caption">
                          Ejemplo con tipo <strong>manual</strong>.
                        </figcaption>
                      </figure>
                      <h4 className="usage-guide-variant-heading">Misma variante por ruta</h4>
                      <figure className="usage-guide-figure">
                        <img
                          src={servicesGuideManualRoute}
                          alt="Paso 2: tipo manual con origen y destino elegidos como rutas."
                          className="usage-guide-screenshot"
                          loading="lazy"
                          decoding="async"
                        />
                        <figcaption className="usage-guide-figure-caption">
                          <strong>Manual</strong> con <strong>origen</strong> y/o <strong>destino</strong>{' '}
                          definidos como <strong>rutas</strong> en el catálogo.
                        </figcaption>
                      </figure>
                    </section>
                  </div>
                </div>
              </details>
            </li>
            <li className="usage-guide-step">
              <details>
                <summary>
                  <span className="usage-guide-step-num">4</span>
                  Paso 3 — Asignación de recursos
                </summary>
                <div className="usage-guide-step-body">
                  <p>
                    Opcionalmente asigna <strong>prioridad</strong>, <strong>conductor</strong>,{' '}
                    <strong>autorizador</strong>, <strong>solicitante</strong> y{' '}
                    <strong>centro de costo</strong>. Puedes dejarlos vacíos y completarlos después.
                  </p>
                  <figure className="usage-guide-figure">
                    <img
                      src={servicesGuideStep3Modal}
                      alt="Modal Crear Nuevo Servicio: Paso 3 de 3, asignación opcional de recursos."
                      className="usage-guide-screenshot"
                      loading="lazy"
                      decoding="async"
                    />
                    <figcaption className="usage-guide-figure-caption">
                      Asistente en <strong>Paso 3 de 3</strong>: prioridad, conductor, autorizador,
                      solicitante y centro de costo (opcional).
                    </figcaption>
                  </figure>
                </div>
              </details>
            </li>
            <li className="usage-guide-step">
              <details>
                <summary>
                  <span className="usage-guide-step-num">5</span>
                  Guardar y volver a la lista
                </summary>
                <div className="usage-guide-step-body">
                  <p>
                    En el último paso usa <strong>Guardar</strong> para crear el servicio. Tras un
                    guardado correcto volverás a la lista de servicios. <strong>Cancelar</strong> cierra
                    el asistente sin guardar.
                  </p>
                </div>
              </details>
            </li>
          </ol>
          <div className="usage-guide-actions">
            <Link to="/services" className="usage-guide-link">
              Ir a Servicios
            </Link>
          </div>
        </section>
      )}

      {tab === 'passenger' && (
        <section
          id={`${baseId}-panel-passenger`}
          role="tabpanel"
          aria-labelledby={`${baseId}-tab-passenger`}
          className="usage-guide-panel"
        >
          <h2>Agregar un pasajero</h2>
          <p className="usage-guide-panel-lead">
            Los pasajeros se administran en la pantalla Pasajeros y se asocian a una empresa.
          </p>
          <ol className="usage-guide-steps">
            <li className="usage-guide-step">
              <details open>
                <summary>
                  <span className="usage-guide-step-num">1</span>
                  Abrir el alta de pasajero
                </summary>
                <div className="usage-guide-step-body">
                  <p>
                    Ve a <strong>Pasajeros</strong> en el menú y pulsa <strong>+ Nuevo Pasajero</strong>.
                    Se cargará el formulario en un modal con el título <strong>Crear pasajero</strong>.
                  </p>
                  <figure className="usage-guide-figure">
                    <img
                      src={passengerGuideList}
                      alt="Pantalla Pasajeros: menú lateral, listado y botón Nuevo Pasajero."
                      className="usage-guide-screenshot"
                      loading="lazy"
                      decoding="async"
                    />
                    <figcaption className="usage-guide-figure-caption">
                      Lista de pasajeros: accede desde el menú y usa <strong>+ Nuevo Pasajero</strong>.
                    </figcaption>
                  </figure>
                </div>
              </details>
            </li>
            <li className="usage-guide-step">
              <details>
                <summary>
                  <span className="usage-guide-step-num">2</span>
                  Completar datos y empresa
                </summary>
                <div className="usage-guide-step-body">
                  <p>
                    Rellena los datos personales (nombre, apellidos, teléfono, correo, etc.) y selecciona
                    la <strong>empresa</strong> a la que pertenece el pasajero en caso de ser necesario puedes crear al pasajero como solicitante y/o autorizador.
                  </p>
                  <figure className="usage-guide-figure">
                    <img
                      src={passengerGuideForm}
                      alt="Modal Crear pasajero: empresa, nombre, apellidos, celular, email y sección de direcciones."
                      className="usage-guide-screenshot"
                      loading="lazy"
                      decoding="async"
                    />
                    <figcaption className="usage-guide-figure-caption">
                      Modal <strong>Crear pasajero</strong>: empresa y datos de contacto en el mismo
                      formulario.
                    </figcaption>
                  </figure>
                </div>
              </details>
            </li>
            <li className="usage-guide-step">
              <details>
                <summary>
                  <span className="usage-guide-step-num">3</span>
                  Direcciones
                </summary>
                <div className="usage-guide-step-body">
                  <p>
                    Añade las <strong>direcciones</strong> que necesites para ese pasajero (recogidas,
                    destinos u otras referencias según cómo esté configurado el formulario).
                  </p>
                  <p>
                    En el modal <strong>Crear pasajero</strong> usa <strong>+ Agregar dirección</strong>{' '}
                    para añadir filas; puedes quitar una con la <strong>X</strong> junto al campo.
                  </p>
                  <figure className="usage-guide-figure">
                    <img
                      src={passengerGuideAddresses}
                      alt="Sección Direcciones: campos por domicilio y botón Agregar dirección."
                      className="usage-guide-screenshot"
                      loading="lazy"
                      decoding="async"
                    />
                    <figcaption className="usage-guide-figure-caption">
                      Sección <strong>Direcciones</strong> del formulario de pasajero.
                    </figcaption>
                  </figure>
                </div>
              </details>
            </li>
            <li className="usage-guide-step">
              <details>
                <summary>
                  <span className="usage-guide-step-num">4</span>
                  Guardar
                </summary>
                <div className="usage-guide-step-body">
                  <p>
                    Pulsa <strong>Guardar</strong> para registrar al pasajero. Si hay errores de
                    validación, corrígelos en el formulario e inténtalo de nuevo. <strong>Cancelar</strong>{' '}
                    cierra el modal sin guardar.
                  </p>
                </div>
              </details>
            </li>
          </ol>
          <div className="usage-guide-actions">
            <Link to="/passenger" className="usage-guide-link">
              Ir a Pasajeros
            </Link>
            <Link to="/services" className="usage-guide-link usage-guide-link--ghost">
              Ir a Servicios
            </Link>
          </div>
          <p className="usage-guide-tip">
            Consejo: da de alta primero los pasajeros y sus direcciones para agilizar el paso 2 al crear
            servicios para la misma empresa.
          </p>
        </section>
      )}

      {tab === 'update' && (
        <section
          id={`${baseId}-panel-update`}
          role="tabpanel"
          aria-labelledby={`${baseId}-tab-update`}
          className="usage-guide-panel"
        >
          <h2>Actualizar información</h2>
          <p className="usage-guide-panel-lead">
            Puedes modificar un servicio existente o los datos de un pasajero desde sus respectivas listas.
          </p>
          <ol className="usage-guide-steps">
            <li className="usage-guide-step">
              <details open>
                <summary>
                  <span className="usage-guide-step-num">1</span>
                  Actualizar un servicio
                </summary>
                <div className="usage-guide-step-body">
                  <p>
                    En <strong>Servicios</strong>, localiza el servicio en la tabla y usa la acción de{' '}
                    <strong>editar</strong> de la fila. Se abrirá la página de detalle del servicio.
                  </p>
                  <p>
                    Si el estado del servicio permite edición, verás el botón <strong>Editar</strong> en
                    la cabecera. Al pulsarlo se abre el modal de edición con los datos cargados; guarda
                    los cambios desde ese modal.
                  </p>
                </div>
              </details>
            </li>
            <li className="usage-guide-step">
              <details>
                <summary>
                  <span className="usage-guide-step-num">2</span>
                  Actualizar un pasajero
                </summary>
                <div className="usage-guide-step-body">
                  <p>
                    En <strong>Pasajeros</strong>, usa la acción de edición del pasajero en la tabla (o el
                    control que abra el formulario). El modal mostrará <strong>Editar pasajero</strong> con
                    los datos actuales.
                  </p>
                  <p>
                    Modifica lo necesario y pulsa <strong>Guardar</strong>. <strong>Cancelar</strong>{' '}
                    descarta los cambios no guardados.
                  </p>
                </div>
              </details>
            </li>
          </ol>
          <div className="usage-guide-actions">
            <Link to="/services" className="usage-guide-link">
              Ir a Servicios
            </Link>
            <Link to="/passenger" className="usage-guide-link usage-guide-link--ghost">
              Ir a Pasajeros
            </Link>
          </div>
          <p className="usage-guide-tip">
            Si no aparece la opción de editar un servicio, puede deberse al estado del mismo (por ejemplo
            cuando ya no admite cambios). En ese caso revisa el flujo operativo o contacta a un
            administrador.
          </p>
        </section>
      )}

      {tab === 'imageExcel' && (
        <section
          id={`${baseId}-panel-imageExcel`}
          role="tabpanel"
          aria-labelledby={`${baseId}-tab-imageExcel`}
          className="usage-guide-panel"
        >
          <h2>Convertir imagen de planilla a Excel</h2>
          <p className="usage-guide-panel-lead">
            Si recibió la solicitud de transporte como captura o foto (formato El Abra), conviértala aquí
            a un archivo Excel con celdas combinadas listo para importar en Servicios.
          </p>
          <ol className="usage-guide-steps">
            <li>
              <strong>Suba la imagen</strong> de la planilla (ingreso/salida con pasajeros, orígenes y destinos).
            </li>
            <li>
              <strong>Convierta a Excel</strong> — el sistema usa IA para leer la tabla y generar el archivo.
            </li>
            <li>
              <strong>Importe el Excel</strong> desde Servicios → <em>Importar planilla El Abra</em> para crear
              los registros de servicio.
            </li>
          </ol>
          <ImageToExcelConverter />
          <div className="usage-guide-actions" style={{ marginTop: '1.25rem' }}>
            <Link to="/services" className="usage-guide-link">
              Ir a Servicios
            </Link>
          </div>
        </section>
      )}

      <p className="usage-guide-footnote">
        <BookOpen size={14} aria-hidden />
        Esta guía describe el comportamiento habitual de la aplicación; algunos campos pueden variar
        según permisos o configuración de empresa.
      </p>
    </div>
  );
};

export default UsageGuidePage;
